const { useState } = React;
import { groupsOf, useCuePool } from "../cuePool.jsx";
import { bgUrl, dbCreatePoolItem, dbDeletePoolItem, dbUpdatePoolItem, normalizePhotoName } from "../data.jsx";
import { useT } from "../i18n.jsx";
import { findPhotoMatch } from "../model.jsx";
import { usePhotoLibrary } from "../photoLibrary.jsx";
import { COLORS, Plus, SearchIcon, T, Trash2, colorOf } from "../theme.jsx";

// ============================================================== CUE POOL =====
// Reusable VTR / IMMERSIVE / activity / sponsor cues. In a board, the pool
// panel lists these and they are dragged into ON-GROUND / BROADCAST /
// platform columns as ready-made blocks.

const inputStyle = { fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", background: T.panel2, color: T.text, minWidth: 0 };

function PoolRow({ item, groups, photos, canEdit }) {
  const { t } = useT();
  const [name, setName] = useState(item.name || "");
  const [sound, setSound] = useState(item.sound || "");
  const [confirm, setConfirm] = useState(false);
  const save = (patch) => dbUpdatePoolItem(item.id, patch);
  const photo = item.photo ? photos.find((p) => normalizePhotoName(p.name) === normalizePhotoName(item.photo)) : findPhotoMatch(item.name, photos);
  const c = colorOf(item.color || "warmgray");
  return (
    <div data-pool-row={item.id} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", padding: "6px 8px", borderBottom: `1px solid ${T.border}` }}>
      <div title={photo ? photo.name : ""} style={{ width: 44, height: 28, flex: "0 0 auto", borderRadius: 4, background: photo ? `center/cover url(${bgUrl(photo.bg)})` : c.fill, border: `1px solid ${T.border}` }} />
      <input aria-label={t("poolNamePh")} className="rb-input" value={name} disabled={!canEdit} placeholder={t("poolNamePh")}
        onChange={(e) => setName(e.target.value)} onBlur={() => name !== item.name && save({ name })} style={{ ...inputStyle, flex: "2 1 200px", fontWeight: 700 }} />
      <input aria-label={t("poolSoundPh")} className="rb-input" value={sound} disabled={!canEdit} placeholder={t("poolSoundPh")}
        onChange={(e) => setSound(e.target.value)} onBlur={() => sound !== (item.sound || "") && save({ sound })} style={{ ...inputStyle, flex: "1 1 120px" }} />
      <select aria-label={t("poolGroup")} value={item.group || ""} disabled={!canEdit} onChange={(e) => save({ group: e.target.value })} style={{ ...inputStyle, flex: "0 1 130px" }}>
        {groups.map((g) => <option key={g} value={g}>{g}</option>)}
      </select>
      <select aria-label={t("poolColor")} value={item.color || "warmgray"} disabled={!canEdit} onChange={(e) => save({ color: e.target.value })} style={{ ...inputStyle, flex: "0 1 100px" }}>
        {COLORS.map((col) => <option key={col.id} value={col.id}>{col.label || col.id}</option>)}
      </select>
      <select aria-label={t("poolPhoto")} value={item.photo || ""} disabled={!canEdit} onChange={(e) => save({ photo: e.target.value })} style={{ ...inputStyle, flex: "1 1 150px" }}>
        <option value="">{t("poolPhotoAuto")}{!item.photo && photo ? ` (${photo.name})` : ""}</option>
        {photos.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
      </select>
      {canEdit && (confirm ? (
        <span style={{ display: "flex", gap: 4 }}>
          <button className="rb-btn" onClick={() => dbDeletePoolItem(item.id)} style={{ background: T.danger, color: "#fff", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 11.5, cursor: "pointer" }}>{t("deleteBtn")}</button>
          <button className="rb-btn" onClick={() => setConfirm(false)} style={{ background: T.panel2, color: T.text, border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 11.5, cursor: "pointer" }}>{t("cancel")}</button>
        </span>
      ) : (
        <button aria-label={`${t("deleteBtn")}: ${item.name}`} className="rb-btn" onClick={() => setConfirm(true)} style={{ background: "none", border: "none", color: T.danger, cursor: "pointer", padding: 4 }}><Trash2 size={14} /></button>
      ))}
    </div>
  );
}

function CuePoolScreen({ perms }) {
  const { t } = useT();
  const items = useCuePool();
  const { list: photos } = usePhotoLibrary();
  const canEdit = !!perms.canMaster;
  const [query, setQuery] = useState("");
  const [bulkGroup, setBulkGroup] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const groups = groupsOf(items);
  const q = normalizePhotoName(query);
  const shown = (items || []).filter((i) => !q || normalizePhotoName(`${i.name} ${i.group}`).includes(q));
  const nextOrder = () => (items || []).reduce((m, i) => Math.max(m, i.order || 0), 0) + 1;
  const addLines = async (group) => {
    const lines = bulkText.split("\n").map((l) => l.replace(/^\s*[-•]\s*/, "").trim()).filter(Boolean);
    let order = nextOrder();
    for (const name of lines) await dbCreatePoolItem({ name, group, sound: "", color: "warmgray", photo: "", order: order++ });
    setBulkText(""); setBulkGroup(null);
  };
  return (
    <div style={{ padding: 16, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>{t("navCuePool")}</div>
        <div style={{ fontSize: 12.5, color: T.subtext, marginTop: 4 }}>{t("poolIntro")}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 360, margin: "0 auto 14px", background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px" }}>
        <SearchIcon size={14} color={T.subtext} />
        <input aria-label={t("poolSearch")} className="rb-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("poolSearch")}
          style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 13, color: T.text }} />
      </div>
      {items === null ? <div style={{ textAlign: "center", color: T.subtext }}>{t("loading")}</div> : groups.map((g) => {
        const rows = shown.filter((i) => (i.group || "") === g);
        if (q && !rows.length) return null;
        return (
          <section key={g} data-pool-group={g} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: T.panel2 }}>
              <span style={{ fontWeight: 800, fontSize: 13.5, color: T.text }}>{g} <span style={{ color: T.subtext, fontWeight: 600 }}>({rows.length})</span></span>
              {canEdit && (
                <button className="rb-btn" onClick={() => { setBulkGroup(bulkGroup === g ? null : g); setBulkText(""); }}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  <Plus size={12} /> {t("poolAdd")}
                </button>
              )}
            </div>
            {bulkGroup === g && (
              <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                <textarea aria-label={t("poolAddPh")} className="rb-input" autoFocus rows={4} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={t("poolAddPh")}
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "inherit" }} />
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button className="rb-btn" onClick={() => setBulkGroup(null)} style={{ background: T.panel2, color: T.text, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>{t("cancel")}</button>
                  <button className="rb-btn" onClick={() => addLines(g)} disabled={!bulkText.trim()} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: bulkText.trim() ? 1 : 0.5 }}>{t("poolAddBtn")}</button>
                </div>
              </div>
            )}
            {rows.map((it) => <PoolRow key={it.id} item={it} groups={groups} photos={photos} canEdit={canEdit} />)}
            {!rows.length && bulkGroup !== g && <div style={{ padding: 10, fontSize: 12, color: T.subtext }}>{t("poolEmptyGroup")}</div>}
          </section>
        );
      })}
    </div>
  );
}

export { CuePoolScreen };
